import React from 'react';
import { Drawer, Button } from '@chakra-ui/react';
import { X } from 'lucide-react';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onExitComplete?: () => void;
  children: React.ReactNode;
  size?: 'md' | 'lg' | 'full';
  placement?: 'start' | 'end' | 'top' | 'bottom';
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  allowAgentChatInteraction?: boolean;
}

export function isAgentChatInteractionTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest('agent-chat')) return true;

  const root = target.getRootNode();
  const shadowHost = (root as ShadowRoot).host;
  return shadowHost instanceof Element && shadowHost.matches('agent-chat');
}

const AppDrawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  onExitComplete,
  children,
  size = 'md',
  placement = 'end',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  allowAgentChatInteraction = false,
}) => {
  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
      onExitComplete={onExitComplete}
      placement={placement}
      closeOnInteractOutside={closeOnOverlayClick}
      closeOnEscape={closeOnEscape}
      modal={!allowAgentChatInteraction}
      trapFocus={!allowAgentChatInteraction}
      onInteractOutside={(event) => {
        if (allowAgentChatInteraction && isAgentChatInteractionTarget(event.detail.target)) {
          event.preventDefault();
        }
      }}
    >
      <Drawer.Backdrop />
      <Drawer.Positioner p={0} h="100dvh" maxH="100dvh" alignItems="stretch">
        <Drawer.Content
          maxW={getSizeValue(size)}
          h="100%"
          minH="100dvh"
          maxH="100dvh"
          display="flex"
          flexDirection="column"
          position="relative"
          borderRadius={0}
        >
          {showCloseButton && (
            <Drawer.CloseTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                position="absolute"
                top={2}
                right={2}
                zIndex={1}
              >
                <X size={20} />
              </Button>
            </Drawer.CloseTrigger>
          )}
          {children}
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
};

const getSizeValue = (size: 'md' | 'lg' | 'full') => {
  const sizeMap = {
    md: '32rem',
    lg: '48rem',
    full: '100vw',
  };
  return sizeMap[size];
};

export const DrawerHeader: React.FC<React.ComponentProps<typeof Drawer.Header>> = (props) => (
  <Drawer.Header flexShrink={0} {...props} />
);
export const DrawerTitle = Drawer.Title;
export const DrawerBody: React.FC<React.ComponentProps<typeof Drawer.Body>> = (props) => (
  <Drawer.Body flex="1" minH={0} overflowY="auto" {...props} />
);
export const DrawerFooter: React.FC<React.ComponentProps<typeof Drawer.Footer>> = (props) => (
  <Drawer.Footer flexShrink={0} {...props} />
);
export const DrawerCloseTrigger = Drawer.CloseTrigger;

export default AppDrawer;
